import React, { useEffect, useRef, useState } from "react";
import { createDecartClient, models } from "@decartai/sdk";
import { decartToken } from "../Services/ApiServices/chatServices";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Styles/decart-vton.scss";

// LOCAL DEV ONLY: set REACT_APP_DECART_API_KEY in web/.env.local (gitignored) to bypass /decart-token.
// NEVER commit a real key. NEVER deploy with this set.
const LOCAL_DEV_API_KEY = process.env.REACT_APP_DECART_API_KEY || "";
console.log("[VTON module] loaded", {
  hasLocalDevKey: !!LOCAL_DEV_API_KEY,
  localDevKeyPrefix: LOCAL_DEV_API_KEY?.substring(0, 3),
  nodeEnv: process.env.NODE_ENV,
});

const CAMERA_STARTUP_TIMEOUT = 30000;
const VIDEO_READY_TIMEOUT = 5000;
const WS_CONNECT_TIMEOUT = 10000;
const REMOTE_STREAM_TIMEOUT = 8000;
const WS_RETRY_ATTEMPTS = 3;
const WS_RETRY_BASE_DELAY = 1500;
const MODEL_FALLBACK_CHAIN = [
  "lucy-vton-latest",
  "lucy-latest",
];

// Safari WebRTC handshakes hang on lucy-vton-latest; try general-purpose models first.
// lucy-2.1-vton is intentionally omitted — Decart's server returns MODEL_NOT_FOUND for it.
const SAFARI_MODEL_FALLBACK_CHAIN = [
  "lucy-restyle-latest",
  "lucy-latest",
];

const isWsError = (err) => {
  const msg = err?.message || "";
  const name = err?.name || "";
  return (
    name === "WebSocket error" ||
    msg.includes("WebSocket") ||
    msg.includes("WebSocket error") ||
    msg.includes("socket") ||
    msg.includes("ECONNREFUSED") ||
    msg.includes("NetworkError") ||
    msg.includes("connection timed out")
  );
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as Mac; detect via touch points as fallback.
  return /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
};

const isSecureContextAvailable = () => {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return true;
  const host = window.location?.hostname || "";
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
};

const isSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /^((?!chrome|android|crios|fxios).)*safari/i.test(ua) || isIOS();
};

const isIOSWebView = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Standalone iOS home-screen apps and native WebViews lack "Safari" in UA.
  return isIOS() && !/Safari/.test(ua);
};

// Direct WebSocket probe to Decart's edge — most accurate way to know if Safari can reach it.
// The WS will be closed by the server (401/403) but we only care whether the TCP+TLS+HTTP-upgrade succeeded.
const preflightDecartReachable = (timeoutMs = 4000) =>
  new Promise((resolve) => {
    if (typeof WebSocket === "undefined") {
      resolve({ ok: false, reason: "no-websocket" });
      return;
    }
    let done = false;
    let ws;
    const finish = (result) => {
      if (done) return;
      done = true;
      try {
        ws?.close();
      } catch (_) {
        /* ignore */
      }
      resolve(result);
    };
    const timer = setTimeout(() => finish({ ok: false, reason: "timeout" }), timeoutMs);
    try {
      ws = new WebSocket("wss://api3.decart.ai/v1/stream");
      ws.onopen = () => {
        clearTimeout(timer);
        finish({ ok: true });
      };
      ws.onerror = () => {
        clearTimeout(timer);
        // In Safari, onerror fires even on 401/403 responses; that still proves reachability.
        finish({ ok: true, note: "error-but-reachable" });
      };
      ws.onclose = (evt) => {
        clearTimeout(timer);
        // A close with any code (even 1006 abnormal) after the socket has attempted upgrade = we reached the host.
        finish({ ok: true, closeCode: evt?.code, note: "closed-during-probe" });
      };
    } catch (err) {
      clearTimeout(timer);
      finish({ ok: false, reason: err?.name || "throw", message: err?.message });
    }
  });

// Decart's SDK only configures a STUN server (no TURN relay), so a real-time session
// requires direct UDP connectivity end-to-end. Some networks/carriers/VPNs silently
// block or filter UDP, which causes ICE negotiation to hang indefinitely (no error,
// just silence) until an external timeout fires. Run a quick local ICE gathering test
// against the same STUN server so we can detect this upfront and fail fast with a
// clear message instead of burning through 3 blind model retries.
const testIceConnectivity = (timeoutMs = 5000) =>
  new Promise((resolve) => {
    if (typeof RTCPeerConnection === "undefined") {
      resolve({ ok: true, reason: "no-rtcpeerconnection-skip-test" });
      return;
    }
    const candidateTypes = new Set();
    let pc;
    let done = false;
    const finish = (result) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try {
        pc?.close();
      } catch (_) {
        /* ignore */
      }
      resolve(result);
    };
    const timer = setTimeout(() => {
      finish({
        ok: candidateTypes.has("srflx") || candidateTypes.has("relay"),
        candidateTypes: Array.from(candidateTypes),
        reason: candidateTypes.size === 0 ? "no-candidates" : "host-only",
      });
    }, timeoutMs);

    try {
      pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
      pc.createDataChannel("probe");
      pc.onicecandidate = (evt) => {
        if (!evt.candidate) {
          // Null candidate = gathering complete.
          finish({
            ok: candidateTypes.has("srflx") || candidateTypes.has("relay"),
            candidateTypes: Array.from(candidateTypes),
            reason: "gathering-complete",
          });
          return;
        }
        const match = /typ (\w+)/.exec(evt.candidate.candidate || "");
        if (match) candidateTypes.add(match[1]);
        if (candidateTypes.has("srflx") || candidateTypes.has("relay")) {
          finish({ ok: true, candidateTypes: Array.from(candidateTypes), reason: "srflx-found" });
        }
      };
      pc.createOffer().then((offer) => pc.setLocalDescription(offer)).catch((err) => {
        finish({ ok: false, reason: "offer-failed", message: err?.message });
      });
    } catch (err) {
      finish({ ok: false, reason: err?.name || "throw", message: err?.message });
    }
  });

// Reorder m=video payload types so H.264 is preferred; required on Safari where VP8 encoding is unreliable.
const preferH264InSdp = (sdp) => {
  if (!sdp) return sdp;
  const lines = sdp.split(/\r\n/);
  const mVideoIdx = lines.findIndex((l) => l.startsWith("m=video"));
  if (mVideoIdx === -1) return sdp;

  const h264Pts = new Set();
  for (const line of lines) {
    const match = /^a=rtpmap:(\d+)\s+H264\//i.exec(line);
    if (match) h264Pts.add(match[1]);
  }
  if (h264Pts.size === 0) return sdp;

  const mLineParts = lines[mVideoIdx].split(" ");
  const header = mLineParts.slice(0, 3);
  const pts = mLineParts.slice(3);
  const preferred = pts.filter((pt) => h264Pts.has(pt));
  const rest = pts.filter((pt) => !h264Pts.has(pt));
  lines[mVideoIdx] = [...header, ...preferred, ...rest].join(" ");
  return lines.join("\r\n");
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const DecartRealtimeVTON = () => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const processedStreamRef = useRef(null);
  const realtimeClientRef = useRef(null);
  const clientRef = useRef(null);
  const startupAbortRef = useRef(false);
  const lastConnectErrorRef = useRef(null);
  const remoteStreamReceivedRef = useRef(false);
  const isCameraOnRef = useRef(false);

  const [isReady, setIsReady] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("user");
  const [prompt, setPrompt] = useState("");
  const [garmentFile, setGarmentFile] = useState(null);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState({ video: [], audio: [] });
  const [status, setStatus] = useState("");
  const [debugLog, setDebugLog] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const startTimeRef = useRef(0);

  const logDebug = (msg) => {
    console.log("[VTON status]", msg);
    setStatus(msg);
    setDebugLog((prev) => {
      const next = [...prev, `${new Date().toISOString().substr(11, 8)} ${msg}`];
      return next.slice(-30);
    });
  };

  const getDefaultVTONPrompt = () =>
    "Substitute the current top with a navy blue hoodie with a white cross logo on the chest";

  useEffect(() => {
    console.log("[VTON] mount: initializing Decart + enumerating devices", {
      isIOS: isIOS(),
      isSafari: isSafari(),
      secureContext: isSecureContextAvailable(),
      href: typeof window !== "undefined" ? window.location.href : null,
    });
    initDecart().catch(() => {
      /* Error already logged + surfaced via setError; user can retry via Open Camera. */
    });
    enumerateDevices();
    return () => {
      console.log("[VTON] unmount cleanup");
    };
  }, []);

  useEffect(() => {
    isCameraOnRef.current = isCameraOn;
  }, [isCameraOn]);

  useEffect(() => {
    if (!isStarting) {
      setElapsedSec(0);
      return;
    }
    startTimeRef.current = Date.now();
    const id = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 500);
    return () => clearInterval(id);
  }, [isStarting]);

  useEffect(() => {
    if (error) setShowDebug(true);
  }, [error]);

  // iOS Safari suspends JS timers when the page is backgrounded, which can cause 25s timeouts to fire minutes later.
  // Abort any in-flight startup when the tab is hidden so a resume shows a clean error, not stale success/failure.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVisibility = () => {
      if (document.hidden && isStarting) {
        console.warn("[VTON] page hidden during startup — aborting to avoid stale timers");
        logDebug("Page backgrounded — aborting startup");
        startupAbortRef.current = true;
        setIsStarting(false);
        setError("Startup was interrupted (page went to background). Please tap Retry.");
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [isStarting]);

  const enumerateDevices = async () => {
    console.log("[VTON] enumerateDevices: start");
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const video = allDevices.filter((d) => d.kind === "videoinput");
      const audio = allDevices.filter((d) => d.kind === "audioinput");
      console.log("[VTON] enumerateDevices: found", {
        videoCount: video.length,
        audioCount: audio.length,
      });
      setDevices({ video, audio });
    } catch (err) {
      console.warn("[VTON] enumerateDevices failed:", err);
    }
  };

  const initDecart = () =>
    new Promise((resolve, reject) => {
      if (LOCAL_DEV_API_KEY) {
        console.warn(
          "[VTON] initDecart: using LOCAL_DEV_API_KEY (bypassing /decart-token). DO NOT DEPLOY."
        );
        try {
          clientRef.current = createDecartClient({ apiKey: LOCAL_DEV_API_KEY });
          console.log("[VTON] initDecart: Decart client created with local key");
          resolve();
        } catch (err) {
          console.error("[VTON] initDecart: local key init failed", err);
          setError("Failed to initialize Decart client with local key");
          reject(err);
        }
        return;
      }

      console.log("[VTON] initDecart: requesting token from /decart-token");
      decartToken(
        (response) => {
          const token = response?.data?.client_token;
          console.log("[VTON] initDecart: token received", {
            hasToken: !!token,
            prefix: token?.substring(0, 3),
            length: token?.length,
          });
          if (!token) {
            setError("No Decart token received from backend");
            reject(new Error("No Decart token received"));
            return;
          }
          try {
            clientRef.current = createDecartClient({ apiKey: token });
            console.log("[VTON] initDecart: Decart client created");
            resolve();
          } catch (err) {
            console.error("[VTON] initDecart: client create failed", err);
            setError("Failed to initialize Decart client");
            reject(err);
          }
        },
        (err) => {
          const status = err?.response?.status;
          const data = err?.response?.data;
          console.error("[VTON] initDecart: token endpoint error", {
            status,
            data,
            message: err?.message,
          });
          setError(
            `Failed to fetch Decart token${status ? ` (HTTP ${status})` : ""}. ` +
              "Check that the API server is running and reachable from this device."
          );
          reject(err);
        }
      );
    });
  const requestMediaWithTimeout = async (constraints, timeoutMs) => {
    console.log("[VTON] requestMediaWithTimeout: start", { constraints, timeoutMs });
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        console.warn("[VTON] requestMediaWithTimeout: TIMEOUT", timeoutMs);
        reject(
          new Error(
            `Camera startup timed out after ${timeoutMs / 1000}s. The camera may be unavailable, in use, or blocked in this environment.`
          )
        );
      }, timeoutMs);
    });

    try {
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        timeoutPromise,
      ]);
      clearTimeout(timeoutId);
      console.log("[VTON] requestMediaWithTimeout: got stream", {
        tracks: stream.getTracks().map((t) => ({
          kind: t.kind,
          label: t.label,
          readyState: t.readyState,
          enabled: t.enabled,
        })),
      });
      return stream;
    } catch (err) {
      clearTimeout(timeoutId);
      console.error("[VTON] requestMediaWithTimeout: error", err?.name, err?.message);
      throw err;
    }
  };

  const waitForVideoReady = async (videoEl, timeoutMs) => {
    console.log("[VTON] waitForVideoReady: start", { timeoutMs });
    if (!videoEl) {
      throw new Error("Video element is not available");
    }
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        console.warn("[VTON] waitForVideoReady: TIMEOUT");
        cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
        reject(
          new Error(
            "Video stream did not become ready. The camera may not be delivering frames in this environment."
          )
        );
      }, timeoutMs);

      const onLoadedMeta = async () => {
        console.log("[VTON] waitForVideoReady: loadedmetadata", {
          videoWidth: videoEl.videoWidth,
          videoHeight: videoEl.videoHeight,
        });
        try {
          await videoEl.play();
          console.log("[VTON] waitForVideoReady: play() resolved");
          cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
          clearTimeout(timeoutId);
          resolve();
        } catch (playErr) {
          console.error("[VTON] waitForVideoReady: play() failed", playErr);
          cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
          clearTimeout(timeoutId);
          reject(
            new Error(
              "Video metadata loaded but playback failed: " + playErr.message
            )
          );
        }
      };

      const onCanPlay = () => {
        console.log("[VTON] waitForVideoReady: canplay");
        cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
        clearTimeout(timeoutId);
        resolve();
      };

      const onError = (e) => {
        console.error("[VTON] waitForVideoReady: video element error", e);
        cleanupVideoReadyListeners(videoEl, onLoadedMeta, onCanPlay, onError);
        clearTimeout(timeoutId);
        reject(
          new Error("Video element encountered an error while loading the stream.")
        );
      };

      videoEl.addEventListener("loadedmetadata", onLoadedMeta);
      videoEl.addEventListener("canplay", onCanPlay);
      videoEl.addEventListener("error", onError);
    });
  };

  const cleanupVideoReadyListeners = (videoEl, ...listeners) => {
    videoEl?.removeEventListener("loadedmetadata", listeners[0]);
    videoEl?.removeEventListener("canplay", listeners[1]);
    videoEl?.removeEventListener("error", listeners[2]);
  };

  const connectToDecart = async (stream, initialState) => {
    console.log("[VTON] connectToDecart: start", {
      streamActive: !!stream,
      trackCount: stream?.getTracks().length,
      hasInitialState: !!initialState,
      hasPrompt: !!initialState?.prompt?.text,
      hasImage: !!initialState?.image,
    });
    lastConnectErrorRef.current = null;

    const chain = isSafari() ? SAFARI_MODEL_FALLBACK_CHAIN : MODEL_FALLBACK_CHAIN;
    console.log("[VTON] connectToDecart: using model chain", chain);
    logDebug(`Using ${isSafari() ? "Safari" : "default"} model chain`);

    for (let attempt = 0; attempt < WS_RETRY_ATTEMPTS; attempt++) {
      console.log(`[VTON] connectToDecart: attempt ${attempt + 1}/${WS_RETRY_ATTEMPTS}`);
      if (startupAbortRef.current) {
        console.warn("[VTON] connectToDecart: aborted by user");
        throw new Error("Connection cancelled by user");
      }

      for (let modelIdx = 0; modelIdx < chain.length; modelIdx++) {
        const modelName = chain[modelIdx];
        console.log(`[VTON] connectToDecart: trying model ${modelName}`);
        logDebug(
          `Attempt ${attempt + 1}/${WS_RETRY_ATTEMPTS} · model ${modelName}`
        );
        const model = models.realtime(modelName);

        if (startupAbortRef.current) {
          throw new Error("Connection cancelled by user");
        }

        remoteStreamReceivedRef.current = false;

        try {
          console.warn(
            `[DecartRealtimeVTON] Attempt ${attempt + 1}/${WS_RETRY_ATTEMPTS}, model: ${modelName}`
          );
          logDebug(`Connecting to ${modelName}...`);

          let connectionTimedOut = false;

          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
              connectionTimedOut = true;
              const msg = `WebSocket connection timed out after ${WS_CONNECT_TIMEOUT / 1000}s`;
              console.error(`[DecartRealtimeVTON] ${msg}`);
              logDebug(`⏱ TIMEOUT: ${msg}`);
              reject(new Error(msg));
            }, WS_CONNECT_TIMEOUT);
          });

          const connectPromise = clientRef.current.realtime.connect(stream, {
            model,
            initialState,
            customizeOffer: async (offer) => {
              // Safari's VP8 encoder is unreliable; force H.264 preference in the SDP.
              if (isSafari() && offer?.sdp) {
                const original = offer.sdp;
                const modified = preferH264InSdp(original);
                if (modified !== original) {
                  offer.sdp = modified;
                  console.log("[DecartRealtimeVTON] Applied H.264 preference for Safari");
                }
              }
            },
            onRemoteStream: (editedStream) => {
              const remoteTracks = editedStream.getTracks();
              const remoteVideoTrack = editedStream.getVideoTracks()[0];
              const remoteSettings = remoteVideoTrack?.getSettings?.() || {};
              console.log("[VTON] Remote (Decart-processed) stream received", {
                tracks: remoteTracks.map((t) => `${t.kind}:${t.readyState}`),
                remoteVideo: {
                  width: remoteSettings.width,
                  height: remoteSettings.height,
                  frameRate: remoteSettings.frameRate,
                  aspectRatio:
                    remoteSettings.width && remoteSettings.height
                      ? remoteSettings.width / remoteSettings.height
                      : null,
                },
              });
              remoteStreamReceivedRef.current = true;
              processedStreamRef.current = editedStream;
              const videoEl = videoRef.current;
              if (!videoEl) return;
              try {
                videoEl.srcObject = null;
              } catch (_) {
                /* ignore */
              }
              videoEl.srcObject = editedStream;
              videoEl.muted = true;
              videoEl.setAttribute("playsinline", "true");
              videoEl.setAttribute("webkit-playsinline", "true");
              const tryPlay = () =>
                videoEl.play().catch((e) => {
                  console.warn("Remote stream play failed:", e);
                });
              tryPlay();
              const logRemoteDims = () => {
                console.log("[VTON] Remote video element playing", {
                  videoWidth: videoEl.videoWidth,
                  videoHeight: videoEl.videoHeight,
                });
              };
              videoEl.addEventListener("loadedmetadata", () => {
                tryPlay();
                logRemoteDims();
              }, { once: true });
              videoEl.addEventListener("canplay", tryPlay, { once: true });
              setIsReady(true);
            },
          });

          realtimeClientRef.current = await Promise.race([
            connectPromise,
            timeoutPromise,
          ]);

          if (realtimeClientRef.current) {
            console.log(
              `[DecartRealtimeVTON] ✓ WebSocket + WebRTC connected on model ${modelName}`
            );
            logDebug(`✓ Connected: ${modelName}`);
            
            // Attach event listeners on the returned client for visibility (esp. Safari).
            const rc = realtimeClientRef.current;
            if (typeof rc.on === "function") {
              rc.on("error", (e) => {
                console.error("[Decart SDK error event]", e?.message || e);
                logDebug("SDK error: " + (e?.message || e));
              });
              rc.on("connectionChange", (state) => {
                console.log("[Decart connectionChange]", state);
                logDebug("Decart state: " + state);
                if (state === "disconnected" || state === "failed") {
                  setError(
                    "Decart connection lost. Reconnecting..."
                  );
                  // Auto-reconnect if the session was actively running.
                  if (isCameraOnRef.current && !startupAbortRef.current) {
                    console.log("[VTON] auto-reconnect triggered");
                    setTimeout(() => {
                      if (isCameraOnRef.current && !startupAbortRef.current) {
                        stopCamera();
                        startCamera();
                      }
                    }, 1500);
                  }
                }
              });
              rc.on("diagnostic", (evt) => {
                if (
                  evt?.name === "peerConnectionStateChange" ||
                  evt?.name === "iceStateChange" ||
                  evt?.name === "phaseTiming" ||
                  evt?.name === "videoStall"
                ) {
                  console.log(`[Decart diag:${evt.name}]`, evt.data);
                }
              });
            }
            console.log(
              `[DecartRealtimeVTON] Connected on ${modelName}, waiting for remote stream...`
            );
            logDebug(`WS connected · waiting for AI video (${modelName})`);

            // Watchdog: if the AI processed stream never arrives, treat as failure and try fallback.
            const remoteStreamDeadline = Date.now() + REMOTE_STREAM_TIMEOUT;
            while (
              !remoteStreamReceivedRef.current &&
              Date.now() < remoteStreamDeadline
            ) {
              if (startupAbortRef.current) {
                throw new Error("Connection cancelled by user");
              }
              await sleep(200);
            }

            if (!remoteStreamReceivedRef.current) {
              console.warn(
                `[DecartRealtimeVTON] No remote stream from ${modelName} within ${REMOTE_STREAM_TIMEOUT / 1000}s`
              );
              try {
                realtimeClientRef.current.disconnect();
              } catch (_) {
                /* ignore */
              }
              realtimeClientRef.current = null;
              throw new Error(
                `Decart AI did not send a processed video stream within ${REMOTE_STREAM_TIMEOUT / 1000}s (model: ${modelName})`
              );
            }

            console.log(
              `[DecartRealtimeVTON] Remote stream confirmed on model: ${modelName}`
            );
            return modelName;
          }
        } catch (err) {
          lastConnectErrorRef.current = err;
          console.error(
            `[DecartRealtimeVTON] Connect failed (model: ${modelName}):`,
            err?.message || err
          );
          const shortMsg = (err?.message || String(err)).slice(0, 120);
          
          // Add Safari-specific diagnostic for timeout
          let debugMsg = `✗ ${modelName}: ${shortMsg}`;
          if (isSafari() && shortMsg.includes("timed out")) {
            debugMsg += ` [Safari WebRTC issue - codec/ICE negotiation slow]`;
          }
          logDebug(debugMsg);

          if (realtimeClientRef.current) {
            try {
              realtimeClientRef.current.disconnect();
            } catch (disconnectErr) {
              console.warn("Error during disconnect cleanup:", disconnectErr);
            }
            realtimeClientRef.current = null;
          }

          const isNoStream = err?.message?.includes("did not send a processed video stream");
          const isRetryable = isWsError(err) || err?.code === "MODEL_NOT_FOUND" || isNoStream;
          if (isRetryable && modelIdx < chain.length - 1) {
            console.warn(
              `[DecartRealtimeVTON] ${err?.code || (isNoStream ? "NO_REMOTE_STREAM" : "WebSocket error")} for model ${modelName}, trying fallback...`
            );
            continue;
          }
        }
      }

      if (startupAbortRef.current) {
        throw new Error("Connection cancelled by user");
      }

      if (attempt < WS_RETRY_ATTEMPTS - 1) {
        const delay =
          WS_RETRY_BASE_DELAY * Math.pow(2, attempt) +
          Math.random() * 500;
        console.warn(
          `[DecartRealtimeVTON] Retrying all models in ${Math.round(delay)}ms...`
        );
        logDebug(`Retrying all models in ${Math.round(delay / 1000)}s...`);
        await sleep(delay);
      }
    }

    const lastErr = lastConnectErrorRef.current;
    logDebug(`━━━ All attempts failed ━━━`);
    if (isSafari() && lastErr?.message?.includes("timed out")) {
      logDebug(`Safari timeout: Refresh & retry, or try different network.`);
    }

    throw (
      lastErr ||
      new Error("Failed to connect to Decart after all retries")
    );
  };

  const startCamera = async () => {
    console.log("[VTON] startCamera: invoked", {
      hasClient: !!clientRef.current,
      cameraFacing,
      isIOS: isIOS(),
      isSafari: isSafari(),
    });
    if (!clientRef.current) {
      logDebug("Decart client not ready, retrying init...");
      setError(null);
      setIsStarting(true);
      try {
        await initDecart();
      } catch (err) {
        console.error("[VTON] startCamera: retry initDecart failed", err);
        logDebug("Init failed: " + (err?.message || err));
        setIsStarting(false);
        return;
      }
      if (!clientRef.current) {
        setIsStarting(false);
        setError(
          "Decart client could not be initialized. See console for details."
        );
        return;
      }
      logDebug("Decart client ready");
    }

    if (!isSecureContextAvailable()) {
      console.warn("[VTON] startCamera: not a secure context");
      setError(
        "Camera access requires a secure context. Please open this page over HTTPS (or localhost)."
      );
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(
        "Camera API not available in this browser. On iPhone, please use Safari and ensure the page is served over HTTPS."
      );
      return;
    }

    startupAbortRef.current = false;
    setError(null);
    setIsReady(false);
    setIsStarting(true);
    logDebug("Requesting camera permission...");

    let stream = null;

    try {
      const chain = isSafari() ? SAFARI_MODEL_FALLBACK_CHAIN : MODEL_FALLBACK_CHAIN;
      const model = models.realtime(chain[0]);
      const iOS = isIOS();
      const safari = isSafari();
      const modelAspect = model.width / model.height;

      // Safari (desktop + iOS) hangs getUserMedia if strict width/height/aspectRatio don't match any camera mode.
      // Strategy: acquire with permissive constraints first, then use track.applyConstraints() to tune non-fatally.
      const buildInitialConstraints = () => {
        if (safari) {
          // Safari-safe: only facingMode on mobile, no width/height/aspect at acquisition time.
          return iOS ? { facingMode: cameraFacing } : true;
        }
        // Chrome/Firefox handle strict ideal constraints reliably.
        const c = { facingMode: cameraFacing };
        if (Number.isFinite(model.fps)) c.frameRate = { ideal: model.fps };
        if (Number.isFinite(model.width)) c.width = { ideal: model.width };
        if (Number.isFinite(model.height)) c.height = { ideal: model.height };
        c.aspectRatio = { ideal: modelAspect };
        return c;
      };

      // Video-only avoids iOS's stricter combined camera+mic permission prompts.
      try {
        stream = await requestMediaWithTimeout(
          {
            video: buildInitialConstraints(),
            audio: false,
          },
          CAMERA_STARTUP_TIMEOUT
        );
        logDebug("Camera acquired");
      } catch (constraintErr) {
        if (startupAbortRef.current) return;
        console.warn(
          "[VTON] initial getUserMedia failed, retrying with { video: true }:",
          constraintErr?.name,
          constraintErr?.message
        );
        logDebug("Retrying camera with basic constraints...");
        stream = await requestMediaWithTimeout(
          { video: true, audio: false },
          CAMERA_STARTUP_TIMEOUT
        );
        logDebug("Camera acquired (fallback)");
      }

      if (startupAbortRef.current) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        return;
      }

      // Try to tune the acquired track towards the model's spec. Non-fatal if unsupported.
      const acquiredVideoTrack = stream.getVideoTracks()[0];
      if (acquiredVideoTrack && typeof acquiredVideoTrack.applyConstraints === "function") {
        try {
          await acquiredVideoTrack.applyConstraints({
            width: { ideal: model.width },
            height: { ideal: model.height },
            aspectRatio: { ideal: modelAspect },
            frameRate: { ideal: model.fps },
          });
          console.log("[VTON] applyConstraints: tuned track towards model spec");
        } catch (tuneErr) {
          console.warn(
            "[VTON] applyConstraints failed (non-fatal):",
            tuneErr?.name,
            tuneErr?.message
          );
        }
      }

      streamRef.current = stream;

      // Log the actual delivered track settings vs. what the Decart model expects (crucial for Safari debugging).
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings?.() || {};
        const deliveredAspect =
          settings.width && settings.height
            ? settings.width / settings.height
            : settings.aspectRatio;
        const aspectMismatch =
          deliveredAspect &&
          Math.abs(deliveredAspect - modelAspect) / modelAspect > 0.05;
        console.log("[VTON] delivered camera settings", {
          width: settings.width,
          height: settings.height,
          frameRate: settings.frameRate,
          aspectRatio: deliveredAspect,
          deviceId: settings.deviceId,
        });
        console.log("[VTON] model expected", {
          name: model.name,
          width: model.width,
          height: model.height,
          fps: model.fps,
          aspectRatio: modelAspect,
        });
        if (aspectMismatch) {
          console.warn(
            `[VTON] ASPECT RATIO MISMATCH: camera=${deliveredAspect?.toFixed(3)} vs model=${modelAspect.toFixed(3)}. ` +
              "Decart may silently drop frames. Consider using a different camera or environment."
          );
        }
      }

      const videoEl = videoRef.current;
      if (videoEl) {
        videoEl.srcObject = stream;
        await waitForVideoReady(videoEl, VIDEO_READY_TIMEOUT);
        console.log("[VTON] local video element playing", {
          videoWidth: videoEl.videoWidth,
          videoHeight: videoEl.videoHeight,
          aspectRatio:
            videoEl.videoHeight > 0
              ? videoEl.videoWidth / videoEl.videoHeight
              : null,
        });
        // Second sample after a beat, in case iOS Safari reports 0 immediately.
        setTimeout(() => {
          if (videoRef.current) {
            console.log("[VTON] local video element (sampled +500ms)", {
              videoWidth: videoRef.current.videoWidth,
              videoHeight: videoRef.current.videoHeight,
            });
          }
        }, 500);
      }

      if (startupAbortRef.current) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (videoEl) videoEl.srcObject = null;
        return;
      }

      let initialState = {
        prompt: {
          text: prompt?.trim()?.length > 0 ? prompt : getDefaultVTONPrompt(),
          enhance: false,
        },
      };

      if (garmentFile) {
        const garmentBlob = new Blob(
          [await garmentFile.arrayBuffer()],
          { type: garmentFile.type }
        );
        initialState.image = garmentBlob;
      }

      // Preflight: verify Safari can actually open a WebSocket to Decart before running the SDK's full handshake.
      logDebug("Testing WebSocket to wss://api3.decart.ai...");
      const preflight = await preflightDecartReachable();
      if (preflight.ok) {
        logDebug(
          "WSS reachable" +
            (preflight.closeCode ? ` (close=${preflight.closeCode})` : "")
        );
      } else {
        logDebug(
          `WSS UNREACHABLE: ${preflight.reason || "unknown"}${preflight.message ? " · " + preflight.message : ""}`
        );
        if (isIOSWebView()) {
          logDebug("Detected iOS WebView — likely App Transport Security block");
        }
        // Hard-fail early so we don't wait the full WS_CONNECT_TIMEOUT × models × attempts.
        throw new Error(
          `Cannot reach Decart AI (wss://api3.decart.ai). ${
            isIOSWebView()
              ? "This is a native app WebView. Open the URL in Safari directly, or add App Transport Security exceptions for *.decart.ai."
              : "Your network or firewall is blocking WebSocket traffic. Try a different Wi-Fi or mobile data."
          }`
        );
      }

      // ICE/UDP connectivity test: Decart's SDK only configures STUN (no TURN relay),
      // so the network must allow direct UDP for WebRTC to ever connect. If UDP is
      // blocked, ICE negotiation hangs silently and every model retry times out
      // identically. Detect that upfront instead of burning 3 blind retries.
      logDebug("Testing UDP/ICE connectivity...");
      const iceTest = await testIceConnectivity();
      logDebug(
        `ICE test: ${iceTest.ok ? "OK" : "BLOCKED"} (${iceTest.reason}${
          iceTest.candidateTypes?.length ? `, types=${iceTest.candidateTypes.join(",")}` : ""
        })`
      );
      if (!iceTest.ok) {
        throw new Error(
          "Your network is blocking real-time video (WebRTC/UDP), so live try-on cannot connect. " +
            "Try switching to mobile data, a different Wi-Fi network, or disabling any VPN, then try again."
        );
      }

      logDebug("Connecting to Decart AI...");
      await connectToDecart(stream, initialState);
      console.log("[VTON] startCamera: connectToDecart returned");

      if (startupAbortRef.current) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        return;
      }

      if (!realtimeClientRef.current) {
        throw new Error("Failed to connect to Decart realtime");
      }

      logDebug("Live AI try-on active");
      setIsCameraOn(true);
      setIsStarting(false);
      await enumerateDevices();
    } catch (err) {
      setIsStarting(false);
      setIsCameraOn(false);
      console.error("Camera error:", err);
      let errorMessage = err?.message || "Failed to start camera";
      if (err?.name === "NotReadableError") {
        errorMessage =
          "Could not start video source. Camera may be in use by another application or unavailable. Close other apps using the camera and try again.";
      } else if (err?.name === "NotAllowedError") {
        errorMessage =
          "Camera access denied. On iPhone, go to Settings > Safari > Camera and allow access, then reload this page.";
      } else if (err?.name === "NotFoundError") {
        errorMessage =
          "No camera found. If you are using a remote desktop or virtual environment, camera passthrough may not be available.";
      } else if (err?.name === "OverconstrainedError") {
        errorMessage =
          "The requested camera settings are not supported on this device. Try switching cameras.";
      } else if (
        err?.name === "TypeError" &&
        err?.message?.includes("insecure")
      ) {
        errorMessage =
          "Camera access blocked: Application must be served over HTTPS.";
      } else if (err?.message?.includes("timed out")) {
        errorMessage =
          "Camera startup timed out. If you are in a remote desktop or virtual session, camera passthrough may not be configured. You can try switching cameras or using a different device.";
      } else if (err?.message?.includes("did not send a processed video stream")) {
        errorMessage =
          "Connected to Decart but no AI video was received. This often means the model is unavailable or WebRTC codecs are incompatible with your browser. Please try again or use a different browser.";
      } else if (
        isWsError(err) ||
        err?.message?.includes("WebSocket connection timed out")
      ) {
        if (isIOSWebView()) {
          errorMessage =
            "Could not reach Decart AI from inside this app. The app's network security policy is likely blocking wss://api3.decart.ai. Please open this page in Safari directly, or ask the app team to add App Transport Security exceptions for *.decart.ai.";
        } else {
          errorMessage =
            "WebSocket connection to Decart failed. Your network or firewall may be blocking wss://api3.decart.ai. Try a different network (mobile data, personal Wi-Fi) or contact support.";
        }
      }
      setError(errorMessage);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const cancelStarting = () => {
    console.log("[VTON] cancelStarting: aborting startup");
    startupAbortRef.current = true;
    setIsStarting(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsReady(false);
  };

  const updateOutfit = async () => {
    console.log("[VTON] updateOutfit: start", {
      hasClient: !!realtimeClientRef.current,
      isReady,
      promptLen: prompt?.length,
      hasGarment: !!garmentFile,
    });
    if (!realtimeClientRef.current || !isReady) {
      console.warn("[VTON] updateOutfit: skipped (no client or not ready)");
      logDebug("Update skipped — session not ready");
      return;
    }

    const rc = realtimeClientRef.current;
    const trimmedPrompt = prompt && prompt.trim().length > 0 ? prompt.trim() : "";

    try {
      logDebug("Updating outfit...");

      // Update image first if provided; setImage on Safari propagates more reliably than a combined set().
      if (garmentFile) {
        const garmentBlob = new Blob(
          [await garmentFile.arrayBuffer()],
          { type: garmentFile.type }
        );
        console.log("[VTON] updateOutfit: setImage()", {
          size: garmentBlob.size,
          type: garmentBlob.type,
        });
        if (typeof rc.setImage === "function") {
          await rc.setImage(garmentBlob, trimmedPrompt ? { prompt: trimmedPrompt } : undefined);
        } else {
          await rc.set({
            image: garmentBlob,
            ...(trimmedPrompt ? { prompt: trimmedPrompt } : {}),
          });
        }
        logDebug("Garment image applied");
      } else if (trimmedPrompt) {
        console.log("[VTON] updateOutfit: setPrompt()", { promptLen: trimmedPrompt.length });
        if (typeof rc.setPrompt === "function") {
          await rc.setPrompt(trimmedPrompt);
        } else {
          await rc.set({ prompt: trimmedPrompt });
        }
        logDebug("Prompt applied");
      } else {
        logDebug("Nothing to update (no prompt, no garment)");
        return;
      }

      console.log("[VTON] updateOutfit: completed");
      logDebug("Outfit update sent");
    } catch (err) {
      console.error("[VTON] updateOutfit: error", err);
      logDebug("Update FAILED: " + (err?.message || err));
      setError(err?.message || "Failed to update outfit");
    }
  };

  const stopCamera = () => {
    console.log("[VTON] stopCamera: start");
    startupAbortRef.current = true;
    remoteStreamReceivedRef.current = false;
    if (realtimeClientRef.current) {
      try {
        realtimeClientRef.current.disconnect();
        console.log("[VTON] stopCamera: Decart client disconnected");
      } catch (err) {
        console.warn("[VTON] stopCamera: disconnect error", err);
      }
      realtimeClientRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      console.log("[VTON] stopCamera: local tracks stopped");
      streamRef.current = null;
    }
    processedStreamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsReady(false);
    setIsStarting(false);
  };

  const switchCamera = async () => {
    console.log("[VTON] switchCamera: toggling facingMode", { from: cameraFacing });
    stopCamera();
    setCameraFacing((p) => (p === "user" ? "environment" : "user"));
    await startCamera();
  };

  return (
    <div className="decart-vton-wrapper">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        webkit-playsinline="true"
        muted
      />

      {!isCameraOn && !isStarting && (
        <div className="vton-input-panel">
          <div className="card shadow-lg">
            <div className="card-body">
              <h2 className="text-center mb-3">Virtual Try-On (Realtime)</h2>

              <div className="mb-3">
                <label className="form-label">Garment Reference Image (Optional)</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => setGarmentFile(e.target.files[0])}
                />
                {garmentFile && (
                  <small className="text-muted">Selected: {garmentFile.name}</small>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Outfit Description</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., navy blue hoodie with white logo"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>

              {error && (
                <div className="vton-error">
                  <div>{error}</div>
                  <button
                    type="button"
                    className="primary"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      setError(null);
                      setDebugLog([]);
                      startCamera();
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}
              {devices.video.length === 0 && !error && (
                <div className="vton-warning">
                  No camera device detected. If you are in a remote desktop or
                  virtual environment, camera passthrough may not be available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="vton-controls">
        {isStarting ? (
          <button className="secondary" onClick={cancelStarting}>
            Cancel
          </button>
        ) : !isCameraOn ? (
          <button className="primary" onClick={startCamera}>
            Open Camera
          </button>
        ) : (
          <>
            <button className="secondary" onClick={switchCamera}>
              Switch
            </button>
            <button
              className="primary"
              onClick={updateOutfit}
              disabled={!isReady}
            >
              Update Outfit
            </button>
            <button className="secondary" onClick={stopCamera}>
              Close
            </button>
          </>
        )}
      </div>

      {isStarting && (
        <p className="vton-loading">
          {status ? status : "Starting camera and connecting to AI..."}
          {" "}
          <span style={{ opacity: 0.7 }}>({elapsedSec}s)</span>
        </p>
      )}
      {!isReady && isCameraOn && !isStarting && (
        <p className="vton-loading">
          {status ? status : "Initializing live AI try-on..."}
        </p>
      )}
      {isReady && isCameraOn && (
        <div className="vton-live-indicator">
          <div className="vton-live-dot"></div>
          LIVE
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowDebug((v) => !v)}
        style={{
          position: "fixed",
          bottom: 8,
          right: 8,
          zIndex: 9999,
          padding: "6px 10px",
          fontSize: 12,
          background: "rgba(0,0,0,0.6)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.3)",
          borderRadius: 4,
        }}
      >
        {showDebug ? "Hide debug" : "Debug"}
      </button>

      {showDebug && (
        <div
          style={{
            position: "fixed",
            bottom: 48,
            right: 8,
            left: 8,
            maxHeight: "50vh",
            overflowY: "auto",
            zIndex: 9998,
            background: "rgba(0,0,0,0.85)",
            color: "#0f0",
            padding: 8,
            fontSize: 11,
            fontFamily: "monospace",
            borderRadius: 4,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {debugLog.length === 0
            ? "(no debug logs yet)"
            : debugLog.join("\n")}
        </div>
      )}
    </div>
  );
};

export default DecartRealtimeVTON;
